import { HorizontZeile, SubstratZeile } from '../../data-model';
import { AuflageStreifen } from './grubenbild-abschnitte';
import { symbolfolgeText } from './horizont-farben';

/**
 * Wo die Beschriftung des Grubenbildes steht.
 *
 * Die Rechnung steht hier ohne SVG und ohne Angular, damit sie sich nachmessen
 * laesst. Gezeichnet wird sie in `grubenbild-zeichnen.ts`.
 *
 * **Jedes Horizontkuerzel steht links IN der Wand**, auf Hoehe seiner eigenen
 * Schicht - auch das einer Schicht, die duenner ist als die Schrift
 * (Nutzer-Entscheidung 20.09.2026: „alle horizontlabels links untereinander.
 * nur wenn sie ueberlappen, dann eines etwas nach rechts versetzen").
 * Rechts neben der Wand stehen die Angaben zum Substrat, darueber die
 * Humusauflage.
 *
 * Zwei Wege sind vorher gescheitert. **Links AUSSERHALB der Wand** ist kein
 * Platz frei: Der linke Rand traegt Meter-Pfeil, Maszband und den
 * Erdreich-Streifen; das Kuerzel legte sich zwangslaeufig darueber
 * (Nutzer-Befund 20.09.2026: „fuer schmale horizonte wird das label links
 * aussen rangequetscht"). **Rechts neben der Wand** liegt es den
 * Fuehrungslinien der Substrate im Weg, die von der Wand zur Angabenspur
 * laufen (Nutzer-Befund 20.09.2026 an AhEs).
 *
 * Das Ausweichen geht deshalb zur Seite, nicht in die Hoehe: Das Kuerzel
 * bleibt auf der Mitte seiner Schicht - nur so sagt es, wozu es gehoert - und
 * rueckt hinter das Kuerzel, mit dem es sich sonst ueberlagerte.
 *
 * Erst wo die Treppe aus der Wand hinauslaeuft, bleibt die Spur rechts als
 * Rueckfall; dort weicht ein Kuerzel wie bisher nach unten aus und zieht eine
 * Fuehrungslinie zu seiner Schicht.
 */

/**
 * Hoehe einer Kuerzelzeile in der Wand, in Pixel.
 *
 * Naeher als das beieinander, und zwei Kuerzel ueberlagern sich - dann rueckt
 * das untere zur Seite.
 */
export const WAND_ZEILE = 14;

/** Abstand des ersten Kuerzels von der linken Wandkante. */
export const WAND_EINZUG = 8;

/** Luft zwischen zwei Kuerzeln, die nebeneinander ausweichen. */
export const WAND_LUECKE = 8;

/** Kleinster Abstand zweier Zeilen einer Spur, in Pixel. */
export const ZEILEN_ABSTAND = 12;

/** Wandkante bis zur Kuerzelspur. */
export const SPUR_ABSTAND = 18;

/** Kuerzelspur bis Angabenspur. */
export const SPUR_LUECKE = 12;

/** Schriftgroesse der Kuerzel in der Wand, in der Spur und der Angaben. */
export const SCHRIFT_WAND = 12.5;
export const SCHRIFT_SPUR = 10.5;

/** So breit ist der rechte Rand mindestens, auch wenn wenig beschriftet ist. */
const RAND_RECHTS_WENIGSTENS = 120;

/** Luft hinter der letzten Spur, damit der Text nicht an der Kante klebt. */
const RAND_LUFT = 10;

/**
 * Wie breit ein Text wird, geschaetzt.
 *
 * SVG kennt die wirkliche Breite erst im Layout (`getComputedTextLength`), und
 * das Bild wird gebaut, bevor es im Dokument haengt. Die Schaetzung ist
 * absichtlich etwas grosszuegig: Lieber ein paar Pixel Luft zu viel als ein
 * abgeschnittenes Kuerzel.
 */
export function textBreite(inhalt: string, groesse: number, fett = false): number {
	return inhalt.length * groesse * (fett ? 0.64 : 0.57);
}

/**
 * Wo ein Text links in der Wand anfaengt, gemessen von der linken Wandkante.
 *
 * Gewoehnlich `WAND_EINZUG` - alles steht untereinander an derselben Kante.
 * Liegt in derselben Zeile schon ein Kuerzel, rueckt der Text dahinter: nur so
 * weit nach rechts wie noetig, und nie in die Hoehe. `y` ist die Mitte der
 * Schrift.
 *
 * Dieselbe Regel gilt fuer die Zeile am Wasserstand (Nutzer-Befund
 * 20.09.2026: „steht soweit links, dass er vom Horizontsymbol ueberdeckt
 * wird") - die Kuerzel werden nach ihr gezeichnet und legten sich darueber.
 */
export function wandAusweichenX(schon: WandZeile[], y: number): number {
	let x = WAND_EINZUG;
	schon.forEach(a => {
		if (Math.abs(a.y - y) >= WAND_ZEILE) return;
		x = Math.max(x, a.x + textBreite(a.text, SCHRIFT_WAND, true) + WAND_LUECKE);
	});
	return x;
}

/** Eine Zeile in einer der beiden Spuren. */
export interface SpurZeile {
	/** Wo die Schicht wirklich liegt - dorthin zeigt die Fuehrungslinie. */
	ankerY: number;
	/** Wo der Text steht, nachdem er der Zeile darueber ausgewichen ist. */
	y: number;
	text: string;
	/**
	 * Diese Zeile bekommt KEINE eigene Fuehrungslinie.
	 *
	 * Gesetzt, wo Kuerzel und Angabe zu derselben Zeile gehoeren und auf
	 * derselben Hoehe stehen: Dann genuegt die Linie zum Kuerzel. Zwei Linien je
	 * Zeile ergaben ein Knaeuel, gerade bei der Humusauflage - drei Zeilen aus
	 * einem vier Zentimeter duennen Bereich, also sechs Linien dicht
	 * beieinander (Nutzer-Befund 20.09.2026).
	 */
	ohneLinie?: boolean;
}

/** Ein Kuerzel in der Wand. */
export interface WandZeile {
	/**
	 * Abstand von der LINKEN Wandkante.
	 *
	 * Gewoehnlich `WAND_EINZUG` - alle Kuerzel stehen untereinander an
	 * derselben Kante. Groesser nur dort, wo das Kuerzel einem anderen zur
	 * Seite ausweichen musste.
	 */
	x: number;
	/** Die Mitte der eigenen Schicht; hier wird nie ausgewichen. */
	y: number;
	text: string;
}

/**
 * Die Klammer, die alle Zeilen der Humusauflage zusammenfasst.
 *
 * Ol, Of und Oh liegen im Bild oft nur wenige Zentimeter auseinander, ihre
 * Beschriftungen brauchen aber mehr Platz. Je Zeile eine eigene Linie ergab ein
 * Faecherbuendel (Nutzer-Befund 20.09.2026). Stattdessen fuehrt EINE Linie vom
 * Auflage-Block zu einer senkrechten Klammer, an der die Zeilen stehen.
 *
 * Bei nur einer Auflagezeile gibt es sie nicht - dort genuegt die gewoehnliche
 * Fuehrungslinie.
 */
export interface AuflageKlammer {
	/** Mitte des Auflage-Blocks an der Wand - dorthin zeigt die Linie. */
	ankerY: number;
	/** Oberste und unterste Zeile, jeweils auf Hoehe der Schriftmitte. */
	vonY: number;
	bisY: number;
}

/**
 * Die Flaeche, die die Beschriftung der Humusauflage einnimmt - Kuerzel,
 * Maechtigkeit und Klammer. Ein Klick darauf zeigt die Auflage vergroessert
 * (Nutzer-Wunsch 21.09.2026).
 *
 * `vonX` und `bisX` sind wie `kuerzelX` von der rechten Wandkante gemessen.
 */
export interface AuflageBlock {
	vonX: number;
	bisX: number;
	vonY: number;
	bisY: number;
}

export interface BeschriftungsPlan {
	inDerWand: WandZeile[];
	kuerzel: SpurZeile[];
	angaben: SpurZeile[];
	auflageKlammer: AuflageKlammer | null;
	auflageBlock: AuflageBlock | null;
	/** Abstand der Kuerzelspur von der rechten Wandkante. */
	kuerzelX: number;
	/** Abstand der Angabenspur von der rechten Wandkante. */
	angabenX: number;
	/** So breit muss der rechte Rand sein, damit beide Spuren hineinpassen. */
	randRechts: number;
}

function zahl(v: unknown): number {
	return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

export interface PlanDaten {
	auflage: { zeilen: AuflageStreifen[]; gesamt: number };
	horizonte: HorizontZeile[];
	substrate: SubstratZeile[];
}

/**
 * Rechnet aus, welcher Text wo steht.
 *
 * `oben` ist die Gelaendeoberflaeche in Pixel, `px` der Maszstab, `tiefe` die
 * Aufnahmetiefe in Zentimetern, `wandBreite` die Breite der Grubenwand im
 * Bild - an ihr endet das Ausweichen nach rechts.
 */
export function beschriftungsPlan(
	daten: PlanDaten, px: number, tiefe: number, oben: number, wandBreite: number,
): BeschriftungsPlan {
	const inDerWand: WandZeile[] = [];
	const kuerzel: SpurZeile[] = [];
	const angaben: SpurZeile[] = [];
	const cmZuY = (cm: number) => oben + cm * px;

	// ---- Humusauflage: nach oben gestaffelt ---------------------------------
	// Ol, Of und Oh sind oft nur ein bis zwei Zentimeter dick, im Maszstab also
	// wenige Pixel. Ueber der Gelaendeoberflaeche ist Platz, dorthin weichen sie
	// aus - das Kuerzel in die eine Spur, die Maechtigkeit in die andere.
	// Mehrere Zeilen haengen an einer gemeinsamen Klammer, eine einzelne an
	// ihrer eigenen Linie.
	const mitKlammer = daten.auflage.zeilen.length > 1;
	let by = oben - 1.5;
	daten.auflage.zeilen.forEach(z => {
		const ankerY = oben - (z.unten + z.maechtigkeit / 2) * px;
		kuerzel.push({ ankerY, y: by, text: z.feld, ohneLinie: mitKlammer });
		// Die Maechtigkeit steht in derselben Zeile rechts daneben - die Linie
		// zum Kuerzel genuegt fuer beide.
		angaben.push({ ankerY, y: by, text: `${z.maechtigkeit} cm`, ohneLinie: true });
		by -= 13;
	});

	// Die Zeilen stehen von unten nach oben; `by` ist die Schriftgrundlinie,
	// die Schriftmitte liegt knapp darueber.
	const auflageKlammer: AuflageKlammer | null = mitKlammer
		? {
			ankerY: oben - daten.auflage.gesamt / 2 * px,
			vonY: by + 13 - 3.5,
			bisY: oben - 1.5 - 3.5,
		}
		: null;

	// Die unterste Auflagezeile zaehlt fuer das Ausweichen mit: Ein duenner
	// Horizont gleich unter der Gelaendeoberflaeche saesse sonst auf ihr.
	const start = daten.auflage.zeilen.length ? oben - 1.5 : -99;
	let letztesKuerzel = start;
	let letzteAngabe = start;

	// ---- Horizonte ----------------------------------------------------------
	daten.horizonte.forEach((z, i) => {
		const von = i > 0 ? zahl(daten.horizonte[i - 1].tiefe) : 0;
		const bis = Math.min(zahl(z.tiefe), tiefe);
		if (bis <= von) return;
		const symbole = (z.horizonte ?? []).filter(Boolean);
		if (!symbole.length) return;
		const text = symbolfolgeText(symbole);
		const ankerY = cmZuY(von) + (bis - von) * px / 2;

		// Links an der Wandkante, auf der Mitte der eigenen Schicht. Nur wo das
		// Kuerzel eines schon gesetzten Horizonts in derselben Zeile liegt,
		// rueckt es hinter dieses nach rechts.
		const breite = textBreite(text, SCHRIFT_WAND, true);
		const x = wandAusweichenX(inDerWand, ankerY);
		if (x + breite <= wandBreite - WAND_EINZUG) {
			inDerWand.push({ x, y: ankerY, text });
			return;
		}

		// Die Treppe laeuft aus der Wand hinaus: dann in die Spur rechts, der
		// Zeile darueber ausweichend. Ein Kuerzel wird dabei NIE weggelassen -
		// ein Horizont ohne Namen waere schlimmer als einer, der etwas neben
		// seiner Schicht steht.
		let y = ankerY;
		if (y - letztesKuerzel < ZEILEN_ABSTAND) y = letztesKuerzel + ZEILEN_ABSTAND;
		letztesKuerzel = y;
		kuerzel.push({ ankerY, y, text });
	});

	// ---- Substrate ----------------------------------------------------------
	const wandHoehe = tiefe * px;
	daten.substrate.forEach((z, i) => {
		const von = i > 0 ? zahl(daten.substrate[i - 1].tiefe) : 0;
		const bis = Math.min(zahl(z.tiefe), tiefe);
		if (bis <= von || !z.substrat) return;
		const ankerY = cmZuY(von) + (bis - von) * px / 2;
		let y = ankerY;
		if (y - letzteAngabe < ZEILEN_ABSTAND) y = letzteAngabe + ZEILEN_ABSTAND;
		if (y > oben + wandHoehe) return;
		letzteAngabe = y;
		const teile = [z.substrat];
		if (zahl(z.humusgehalt) > 0) teile.push('h' + z.humusgehalt);
		if (zahl(z.skelettgehalt) > 0) teile.push('Sk' + z.skelettgehalt);
		angaben.push({ ankerY, y, text: teile.join(' ') });
	});

	// ---- Wie breit die Spuren werden ----------------------------------------
	const breiteste = (zeilen: SpurZeile[], fett: boolean) => zeilen
		.reduce((b, z) => Math.max(b, textBreite(z.text, SCHRIFT_SPUR, fett)), 0);
	const kuerzelBreite = breiteste(kuerzel, true);
	const angabenBreite = breiteste(angaben, false);

	// Steht kein Kuerzel in der Spur, rueckt die Angabenspur an die Wand - sonst
	// klaffte eine leere Spalte, wo nie etwas steht.
	const kuerzelX = SPUR_ABSTAND;
	const angabenX = kuerzelBreite > 0
		? SPUR_ABSTAND + kuerzelBreite + SPUR_LUECKE
		: SPUR_ABSTAND;

	// Die Auflage-Zeilen stehen vorn in beiden Listen. Oben und unten ein paar
	// Pixel Luft, damit die Flaeche die Schrift ganz umschliesst und sich mit
	// dem Finger treffen laesst.
	const auflageZeilen = daten.auflage.zeilen.length;
	const auflageBlock: AuflageBlock | null = auflageZeilen
		? {
			vonX: kuerzelX - 10,
			bisX: angabenX + breiteste(angaben.slice(0, auflageZeilen), false) + 4,
			vonY: Math.min(...kuerzel.slice(0, auflageZeilen).map(z => z.y)) - 7,
			bisY: Math.max(...kuerzel.slice(0, auflageZeilen).map(z => z.y)) + 7,
		}
		: null;

	return {
		inDerWand, kuerzel, angaben, auflageKlammer, auflageBlock, kuerzelX, angabenX,
		randRechts: Math.max(RAND_RECHTS_WENIGSTENS, angabenX + angabenBreite + RAND_LUFT),
	};
}
